import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Wallet, TrendingUp, Repeat, AlertTriangle, RotateCcw,
} from "lucide-react";

/**
 * My Income tab — aggregates orders + extra-domain subs from /api/income/overview.
 * Read-only. Replaces the old PaymentsTab as the primary owner-facing revenue view.
 */
const formatMoney = (cents, currency = "usd") => {
  const num = Number(cents || 0) / 100;
  try {
    return num.toLocaleString("en-US", { style: "currency", currency: currency.toUpperCase() });
  } catch {
    return `$${num.toFixed(2)}`;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function Sparkline({ data, height = 60 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data);
  const w = 320;
  const h = height;
  const step = w / (data.length - 1 || 1);
  const points = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 6) - 3).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: h }} data-testid="income-sparkline">
      <polyline fill="none" stroke="#8B5CF6" strokeWidth="2" points={points} />
      <polyline
        fill="rgba(139,92,246,0.12)"
        stroke="none"
        points={`0,${h} ${points} ${w},${h}`}
      />
    </svg>
  );
}

function StatCard({ label, value, sub, testId, accent }) {
  return (
    <div className="nb-card p-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">{label}</div>
      <div className="mt-2 font-display text-3xl font-extrabold" style={accent ? { color: accent } : undefined}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function IncomeTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/income/overview");
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load income data");
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }
  if (!data) return <div className="nb-card p-5 text-sm text-gray-600">No income data yet.</div>;

  const currency = data.currency || "usd";
  const subs = data.subscriptions || { active_count: 0, active_monthly_cents: 0, extra_domains_active: 0, extra_domains_monthly_cents: 0 };
  const byKind = data.by_kind_cents || {};
  const recent = data.recent_transactions || [];
  const spark = data.spark_30d_cents || [];

  return (
    <div data-testid="income-tab">
      <div className="nb-card p-5 mb-6 flex items-center gap-3">
        <Wallet className="w-7 h-7" />
        <div>
          <div className="font-display text-2xl font-extrabold">My Income</div>
          <div className="text-sm text-gray-600 mt-0.5">Lifetime earnings, MRR, and recent transactions across this website.</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="Lifetime revenue"
          value={formatMoney(data.lifetime_cents, currency)}
          sub={`${data.total_paid_orders || 0} paid orders`}
          testId="income-stat-lifetime"
        />
        <StatCard
          label="This month"
          value={formatMoney(data.this_month_cents, currency)}
          testId="income-stat-month"
        />
        <StatCard
          label="Last 30 days"
          value={formatMoney(data.last_30_days_cents, currency)}
          testId="income-stat-30d"
        />
        <StatCard
          label="MRR (estimate)"
          value={formatMoney(data.mrr_cents, currency)}
          sub={`${subs.active_count} subs + ${subs.extra_domains_active} extra domains`}
          accent="#8B5CF6"
          testId="income-stat-mrr"
        />
      </div>

      {/* 30-day spark + breakdown */}
      <div className="grid lg:grid-cols-[2fr_1fr] gap-4 mb-6">
        <div className="nb-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg font-extrabold flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Last 30 days</div>
            <div className="text-xs text-gray-500">Daily revenue · {formatMoney(data.last_30_days_cents, currency)} total</div>
          </div>
          <Sparkline data={spark} />
        </div>
        <div className="nb-card p-5">
          <div className="font-display text-lg font-extrabold flex items-center gap-2 mb-3"><Repeat className="w-5 h-5" /> By kind</div>
          <ul className="text-sm space-y-2" data-testid="income-by-kind">
            <li className="flex justify-between"><span className="text-gray-600">One-time products</span><span className="font-bold">{formatMoney(byKind.product || 0, currency)}</span></li>
            <li className="flex justify-between"><span className="text-gray-600">Services</span><span className="font-bold">{formatMoney(byKind.service || 0, currency)}</span></li>
            <li className="flex justify-between"><span className="text-gray-600">Subscriptions</span><span className="font-bold">{formatMoney(byKind.subscription || 0, currency)}</span></li>
            <li className="flex justify-between border-t border-black/10 pt-2 mt-2"><span className="text-gray-600">Extra-domain subs</span><span className="font-bold">{formatMoney(subs.extra_domains_monthly_cents, currency)} /mo</span></li>
          </ul>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="nb-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-extrabold">Recent transactions</div>
          <div className="text-xs text-gray-500">{recent.length} of last 20 paid</div>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center" data-testid="income-no-transactions">
            No paid orders yet. Once a customer checks out, they&apos;ll show up here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="income-transactions-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="py-2 pr-4 font-bold">When</th>
                  <th className="py-2 pr-4 font-bold">Customer</th>
                  <th className="py-2 pr-4 font-bold">Product</th>
                  <th className="py-2 pr-4 font-bold">Kind</th>
                  <th className="py-2 pr-4 font-bold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((t) => (
                  <tr key={t.order_id} className="border-t border-black/10" data-testid={`income-row-${t.order_id}`}>
                    <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{formatDateTime(t.paid_at)}</td>
                    <td className="py-2 pr-4">
                      <div className="font-semibold">{t.customer_name || "—"}</div>
                      <div className="text-xs text-gray-500">{t.customer_email || ""}</div>
                    </td>
                    <td className="py-2 pr-4">{t.product_name || "—"}</td>
                    <td className="py-2 pr-4 capitalize">{t.kind || "product"}</td>
                    <td className="py-2 pr-4 text-right font-bold">
                      {formatMoney(t.amount_cents, t.currency || currency)}
                      {t.refunded && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest text-rose-600"><RotateCcw className="w-3 h-3" />refund</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Issues / refunds */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="nb-card p-5" data-testid="income-failures">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-amber-600" /> <div className="font-bold">Failed / past due</div></div>
          <div className="font-display text-2xl font-extrabold">{data.failed_count || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Orders that hit a payment error or are past due. Review in the Payments tab.</div>
        </div>
        <div className="nb-card p-5" data-testid="income-refunds">
          <div className="flex items-center gap-2 mb-2"><RotateCcw className="w-5 h-5 text-rose-600" /> <div className="font-bold">Refunded</div></div>
          <div className="font-display text-2xl font-extrabold">{formatMoney(data.refunded?.cents || 0, currency)}</div>
          <div className="text-xs text-gray-500 mt-1">{data.refunded?.count || 0} refund{(data.refunded?.count || 0) === 1 ? "" : "s"} issued.</div>
        </div>
      </div>
    </div>
  );
}
